import format from 'date-fns/format';
import parse from 'date-fns/parse';

/**
 * Formats the string to "--" if string is null
 * @param {string} str - string to format
 */
export function orDash(str: string | null) {
  if (str && str.length) return str;
  return '--';
}

/**
 * Formats title to have first character of each word to be capitalized
 * @param {string} title - title to format
 */
export const formatTitle = (title: string) => {
  return title
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

/**
 * Capitalize only the first word in a sentence
 * @param {string} text - text to format
 */
export const capitalizeFirst = (text: string) => {
  return text.charAt(0).toUpperCase() + text.slice(1);
};

export const formatHardwareTitle = (title: string) => {
  return title
    .split(/(?=[A-Z])/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

export const formatMilitaryTime = (time: number): string => {
  const date = parse(time.toString().padStart(4, '0'), 'HHmm', new Date());
  return format(date, 'h:mm a');
};

/**
 * Alphabetically sort a list of strings or objects.
 * Pass an empty string as `primary` to sort a list of strings.
 * Pass a `primary` and optional `secondary` key to sort a list of objects.
 * The `secondary` key is used to break ties when two objects have the same value for the `primary` key.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getSortedList = (list: any[], primary: string, secondary?: string) => {
  return [...list].sort((a, b) => {
    if (!primary || !primary.length) {
      return a > b ? 1 : -1;
    } else if (a[primary] === b[primary]) {
      if (secondary) {
        return a[secondary] > b[secondary] ? 1 : -1;
      }
      return 0;
    }
    return a[primary] > b[primary] ? 1 : -1;
  });
};

/**
 * Generates a unique name by appending (or incrementing) a numbered suffix,
 * e.g. "Adult" -> "Adult(1)" -> "Adult(2)", based on the `name` fields of `items`.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getUniqueName = (name: string, items: any[]) => {
  let count = 1;
  let uniqueString = '';
  for (let i = 1; i <= count; i++) {
    const occurance = name.indexOf('(');
    if (occurance > 0) {
      uniqueString = `${name.slice(0, occurance)}(${i})`;
    } else {
      uniqueString = `${name}(${i})`;
    }
    // eslint-disable-next-line no-loop-func
    const sameNameItems = items.filter((item) => item.name === uniqueString);
    if (sameNameItems.length > 0) count++;
  }

  return uniqueString;
};

/**
 * Attempts to convert a string to JSON. Will return the JSON
 * if successful, or False if the string is not valid JSON.
 * @param jsonString - string to convert
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const tryToParseObject = (jsonString: string): any => {
  try {
    const result = JSON.parse(jsonString);

    if (result && typeof result === 'object') {
      return result;
    }
  } catch (err) {
    // no-op
  }

  return false;
};
